import React, { useRef, useEffect, useState, useCallback } from 'react';
import { useLang } from "../../contexts/LanguageContext";
import { useNavigate } from 'react-router-dom';
import apiUrls from '../../api/apiUrls';
import { Row, Col } from 'react-bootstrap';
import SettingsOffcanvas from './ChapterSettingsOffcanvas';
import './ChapterMenu.css'

const ChapterMenu = ({
  bookId,
  chapterId,
  bookLanguage,
  chapterName,
  bookShortTitle,
  bookPrintDate,
  previousChapterId,
  nextChapterId,
  previousChapterHash,
  nextChapterHash,
  preferences,
  onProgress,
  onWordChange,
  wordTimings,
  onFontSizeChange,
  onShowLemmaChange,
  onDictLanguageChange,
  onLastWordReached,
  autoPlay,
  onAutoPlayChange,
  onDialectChange,
}) => {
  const { t } = useLang();
  const navigate = useNavigate();

  const audioRef = useRef(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [dragTime, setDragTime] = useState(0);

  const handleSeekEnd = useCallback((e) => {
    const audio = audioRef.current;
    const finalTime = parseFloat(e.target.value);
    
    if (audio) {
      try {
        audio.currentTime = finalTime;
      } catch (err) {
        console.error(`[ChapterMenu] Error al asignar currentTime:`, err);
      }

      setCurrentTime(finalTime);
      
      if (audio.paused) {
        audio.play().then(() => {
          document.getElementById('playPauseButton')?.classList.add('pause');
          document.getElementById('playPauseButton')?.classList.remove('play');
        }).catch((err) => {
          console.warn(`[ChapterMenu] Error al reanudar:`, err);
        });
      }
    }
    setIsDragging(false);
  }, []);

  const handleSeekChange = (e) => {
    const val = parseFloat(e.target.value);
    setDragTime(val);
  };

  useEffect(() => {
    const audio = audioRef.current;

    const handleTimeUpdate = () => {
      if (!isDragging) {
        setCurrentTime(audio.currentTime);
      }
      onProgress?.(audio.currentTime);

      if (onWordChange && wordTimings) {
        const activeWord = wordTimings.find(
          (word) => audio.currentTime >= word.start && audio.currentTime <= word.end
        );

        if (activeWord) {
          onWordChange(activeWord.id);
          if (activeWord.id === wordTimings.length && onLastWordReached && autoPlay) {
            onLastWordReached();
          }
        }
      }
    };

    const handleLoadedMetadata = () => {
      const audioEl = audioRef.current;
      setDuration(audioEl.duration);

      if (autoPlay) {
        audioEl
          .play()
          .then(() => {
            const playPauseButton = document.getElementById('playPauseButton');
            if (playPauseButton) {
              playPauseButton.classList.add('pause');
              playPauseButton.classList.remove('play');
            }
          })
          .catch((error) => {
            console.error('Error al intentar reproducir el audio automáticamente:', error);
          });
      }
    };

    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
    };
  }, [autoPlay, isDragging, onLastWordReached, onProgress, onWordChange, wordTimings]);

  useEffect(() => {
    const audio = audioRef.current;
    const speedValues = [0.5, 0.75, 1, 1.25, 1.5];
    if (preferences && preferences.text_speed !== undefined) {
      audio.playbackRate = speedValues[preferences.text_speed];
    }
  }, [preferences]);

  const handlePlayPause = () => {
    const audio = audioRef.current;
    if (audio.paused) {
      audio.play();
      document.getElementById('playPauseButton')?.classList.add('pause');
      document.getElementById('playPauseButton')?.classList.remove('play');
    } else {
      audio.pause();
      document.getElementById('playPauseButton')?.classList.add('play');
      document.getElementById('playPauseButton')?.classList.remove('pause');
    }
  };

  const handlePrevChapter = (e) => {
    e.preventDefault();
    if (previousChapterHash) {
      navigate(`/chapter?id=${previousChapterHash}`);
    } else if (previousChapterId) {
      navigate(`/chapter?book_id=${bookId}&chapter_id=${previousChapterId}`);
    }
  };

  const handleNextChapter = (e) => {
    e.preventDefault();
    if (nextChapterHash) {
      navigate(`/chapter?id=${nextChapterHash}`);
    } else if (nextChapterId) {
      navigate(`/chapter?book_id=${bookId}&chapter_id=${nextChapterId}`);
    }
  };

  const [show, setShowOffcanvas] = useState(false);
  const handleCloseOffcanvas = () => setShowOffcanvas(false);
  const handleShowOffcanvas = () => setShowOffcanvas(true);

  const book_title_info = `${bookShortTitle} - ${bookPrintDate} (${chapterName})`;

  const formattedDialect = preferences.dialect_code?.replace('-', '_') || 'es_ES';
  const audioUrl = `${apiUrls.audioFiles}${bookId}/${bookId}_${chapterId}_${formattedDialect}.mp3`;

  const handleDialectChange = (newDialect) => {
    const audio = audioRef.current;
    const formattedNewDialect = newDialect.replace('-', '_');
    const newAudioUrl = `${apiUrls.audioFiles}${bookId}/${bookId}_${chapterId}_${formattedNewDialect}.mp3`;
    audio.src = newAudioUrl;

    onDialectChange(newDialect);

    audio.addEventListener(
      'canplaythrough',
      () => {
        audio
          .play()
          .then(() => {
            const playPauseButton = document.getElementById('playPauseButton');
            if (playPauseButton) {
              playPauseButton.classList.add('pause');
              playPauseButton.classList.remove('play');
            }
          })
          .catch((error) => {
            console.error(t("CHAPTER_ERROR_AUTOPLAY_AUDIO"), error);
          });
      },
      { once: true }
    );
  };

  const handleSpeedChange = (newSpeed) => {
    const audio = audioRef.current;
    const speedValues = [0.5, 0.75, 1, 1.25, 1.5];
    audio.playbackRate = speedValues[newSpeed];
  };

  return (
    <nav className="navbar fixed-top navbar-light bg-light reader_navbar" id="header_nav">
      <div className="info_book">
        <div className="container-sm">
          <audio ref={audioRef} src={audioUrl} controls style={{ display: 'none' }} />
          <Row className="g-0">
            <Col xs={12} id="book_title_sm">
              <div >{book_title_info}</div>
            </Col>
            <Col xs={12}>
              <div className="chapter_menu">
                <div className="chapter_menu_left">
                  <a href={`/book?book_id=${bookId}`} id="backLink" className="backLink" title={t("CHAPTER_BACK")}></a>
                  <button id="playPauseButton" title={t("CHAPTER_PLAY")} className="playPauseButton" onClick={handlePlayPause}></button>

                  {previousChapterId && (
                    <a href="#" id="prevLink" className="prevLink" title={t("CHAPTER_PREV")} onClick={handlePrevChapter}></a>
                  )}
                  {nextChapterId && (
                    <a href="#" id="nextLink" className="nextLink" title={t("CHAPTER_NEXT")} onClick={handleNextChapter}></a>
                  )}
                </div>

                <div className="chapter_menu_center">
                  <div id="book_title_lg">{book_title_info}</div>
                </div>

                <div className="chapter_menu_right">
                  <button onClick={handleShowOffcanvas} className="settings_button" type="button" title={t("CHAPTER_SETTINGS_TITLE")}></button>
                </div>
              </div>
            
            </Col>
            <Col xs={12} className='chapter-menu-row3'>
            
              <div id="chapter_time">
                <span id="currentTime">{new Date(currentTime * 1000).toISOString().substr(11, 8)}</span>{' '}/{' '}
                <span id="duration">{new Date(duration * 1000).toISOString().substr(11, 8)}</span>
              </div>  
            
            </Col>
          </Row> 
          
        </div>

        <div id="progressBar">
          <input
            type="range"
            min="0"
            max={duration || 100}
            step="0.01"
            value={isDragging ? dragTime : currentTime}
            onMouseDown={(e) => {
              setDragTime(currentTime);
              setIsDragging(true);
            }}
            onTouchStart={(e) => {
              setDragTime(currentTime);
              setIsDragging(true);
            }}
            onChange={handleSeekChange}
            onMouseUp={handleSeekEnd}
            onTouchEnd={handleSeekEnd}
            className="reader-progress-slider"
            style={{
              '--progress-percent': `${((isDragging ? dragTime : currentTime) / (duration || 1)) * 100}%`
            }}
          />
        </div>
      </div>
      <SettingsOffcanvas
        show={show}
        handleClose={handleCloseOffcanvas}
        preferences={preferences}
        bookLanguage={bookLanguage}
        onDialectChange={onDialectChange}
        onFontSizeChange={onFontSizeChange}
        onShowLemmaChange={onShowLemmaChange}
        onDictLanguageChange={onDictLanguageChange}
        onSpeedChange={handleSpeedChange}
        onAutoPlayChange={onAutoPlayChange}
      />
    </nav>
  );
};

export default ChapterMenu;
